/**
 * Register Page
 * ==============
 * New user and organization registration.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, UserPlus } from 'lucide-react';

import { useRegister } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

// =============================================================================
// VALIDATION
// =============================================================================

const registerSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  confirm_password: z.string(),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  organization_name: z.string().min(2, 'Organization name is required'),
  organization_phone: z.string().optional(),
  accept_terms: z.boolean().refine((val) => val === true, {
    message: 'You must accept the terms of service',
  }),
  accept_privacy: z.boolean().refine((val) => val === true, {
    message: 'You must accept the privacy policy',
  }),
}).refine((data) => data.password === data.confirm_password, {
  message: "Passwords don't match",
  path: ['confirm_password'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

// =============================================================================
// COMPONENT
// =============================================================================

export default function Register() {
  const [showPassword, setShowPassword] = useState(false);
  const { mutate: register, isPending } = useRegister();

  const {
    register: registerField,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      accept_terms: false,
      accept_privacy: false,
    },
  });

  const onSubmit = (data: RegisterFormData) => {
    register(data);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">
            Create Account
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Start your free trial today
          </p>
        </div>

        {/* Form */}
        <Card className="p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First name"
                {...registerField('first_name')}
                error={errors.first_name?.message}
              />
              <Input
                label="Last name"
                {...registerField('last_name')}
                error={errors.last_name?.message}
              />
            </div>

            <Input
              label="Email address"
              type="email"
              {...registerField('email')}
              error={errors.email?.message}
            />

            {/* Password Fields */}
            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                {...registerField('password')}
                error={errors.password?.message}
              />
              <button
                type="button"
                className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <Input
              label="Confirm password"
              type={showPassword ? 'text' : 'password'}
              {...registerField('confirm_password')}
              error={errors.confirm_password?.message}
            />

            {/* Organization Fields */}
            <div className="border-t border-gray-200 pt-5 mt-5">
              <h3 className="text-sm font-medium text-gray-900 mb-4">
                Organization Details
              </h3>
              
              <Input
                label="Organization name"
                {...registerField('organization_name')}
                error={errors.organization_name?.message}
              />

              <div className="mt-4">
                <Input
                  label="Phone (optional)"
                  type="tel"
                  {...registerField('organization_phone')}
                  error={errors.organization_phone?.message}
                />
              </div>
            </div>

            {/* Terms */}
            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  id="accept_terms"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                  {...registerField('accept_terms')}
                />
                <label htmlFor="accept_terms" className="ml-2 block text-sm text-gray-700">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-primary-600 hover:text-primary-500 underline">
                    Terms of Service
                  </Link>
                </label>
              </div>
              {errors.accept_terms && (
                <p className="text-sm text-red-600">{errors.accept_terms.message}</p>
              )}

              <div className="flex items-start">
                <input
                  id="accept_privacy"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                  {...registerField('accept_privacy')}
                />
                <label htmlFor="accept_privacy" className="ml-2 block text-sm text-gray-700">
                  I agree to the{' '}
                  <Link to="/privacy" target="_blank" className="text-primary-600 hover:text-primary-500 underline">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/hipaa" target="_blank" className="text-primary-600 hover:text-primary-500 underline">
                    HIPAA Notice
                  </Link>
                </label>
              </div>
              {errors.accept_privacy && (
                <p className="text-sm text-red-600">{errors.accept_privacy.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              isLoading={isPending}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create account
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Sign in
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
